import { useMemo } from 'react';

import clsxm from '@/lib/clsxm';
import useInterpolation from '@/hooks/use-interpolation';

import HygraphImage, { HygraphImageProps } from '@/components/hygraph-image';
import HygraphRichText from '@/components/hygraph-rich-text';
import Link, { LinkProps } from '@/components/link';
import { RichTextProps } from '@/components/rich-text';
import Typography from '@/components/typography';

import { CardStyle } from '@/enums/card-style';
import { HorizontalAlignment } from '@/enums/horizontal-alignment';
import { ImageWidth } from '@/enums/image-width';
import { Side } from '@/enums/side';
import { VerticalSide } from '@/enums/vertical-side';

import AwardQuoteBgLeft from '~/svg/award-quote-bg-left.svg';
import AwardQuoteBgRight from '~/svg/award-quote-bg-right.svg';

export const CardStylesClassNames = {
  [CardStyle.LIGHT_GREY]: 'bg-grey50 rounded-xs sm:rounded',
  [CardStyle.LIGHT_BLUE]: 'bg-light-blue rounded-xs sm:rounded',
  [CardStyle.LIGHT_PINK]: 'bg-primary-light rounded-xs sm:rounded',
  [CardStyle.LIGHT_PURPLE]: 'bg-light-purple rounded-xs sm:rounded',
  [CardStyle.TRANSPARENT]: 'bg-transparent',
  [CardStyle.WHITE_WITH_BORDER]:
    'bg-white shadow rounded-xs sm:rounded py-6 mb-4 sm:mb-0',
  [CardStyle.AWARD_QUOTE]:
    'bg-transparent relative pl-12 pr-12 mb-4 justify-center h-32',
  [CardStyle.WIDE_WHITE_WITH_BORDER]:
    'bg-white shadow-md rounded-xs sm:rounded',
  [CardStyle.PROFILE_STYLE]:
    'bg-white shadow rounded-xs sm:rounded py-6 mb-4 sm:mb-0 mx-3 sm:mx-0 pb-5 h-[calc(100%-1rem)]',
  [CardStyle.SMALL_WHITE_CARD]:
    'bg-white shadow rounded-xs sm:rounded py-6 px-3 mb-4 sm:mb-0',
  [CardStyle.HORIZONTAL_IMAGE_TEXT]:
    'flex items-center bg-white items-center h-[calc(100%-1rem)] shadow rounded-xs sm:rounded py-8 px-5 mb-4',
  // add new style here for cards on homepage
};

export type CardProps = {
  caption?: string;
  title?: string;
  body?: RichTextProps | string;
  image?: HygraphImageProps;
  imagePosition?: VerticalSide | Side | null | undefined;
  imageWidth?: ImageWidth;
  cardStyle?: CardStyle | null;
  link?: LinkProps;
  cardLink?: LinkProps;
  linkDecorator?: boolean;
  textAlignment?: HorizontalAlignment;
  className?: string;
  isInSidebar?: boolean;
};

const imageContainerClassMap = {
  [VerticalSide.BOTTOM]: 'flex-col-reverse pt-5',
  [VerticalSide.TOP]: 'flex-col pb-5',
  [Side.LEFT]: 'flex-col md:flex-row',
  [Side.RIGHT]: 'flex-col-reverse md:flex-row-reverse',
};

const imageClassMap: Partial<
  Record<CardStyle, Partial<Record<Side | VerticalSide, string>>>
> = {
  [CardStyle.WIDE_WHITE_WITH_BORDER]: {
    [Side.LEFT]: 'w-full md:w-1/2',
    [Side.RIGHT]: 'w-full md:w-1/2',
  },
};

const textAlignmentMap = {
  [HorizontalAlignment.CENTER]: 'text-center',
  [HorizontalAlignment.LEFT]: 'text-left',
  [HorizontalAlignment.RIGHT]: 'text-right',
};

const decoratorAlignmentMap = {
  [HorizontalAlignment.CENTER]: 'justify-center',
  [HorizontalAlignment.LEFT]: 'justify-start',
  [HorizontalAlignment.RIGHT]: 'justify-end',
};

export const Card = ({
  title,
  body,
  caption,
  image,
  imagePosition,
  imageWidth,
  cardStyle,
  link,
  cardLink,
  linkDecorator: withLinkDecorator = false,
  textAlignment = HorizontalAlignment.LEFT,
  className,
  isInSidebar = false,
}: CardProps) => {
  const interpolate = useInterpolation();
  const hasImage = !!image;

  const imagePositionClass = useMemo(() => {
    if (imagePosition) {
      return imageContainerClassMap[imagePosition];
    }

    return 'flex-col pb-1';
  }, [imagePosition]);

  const isHorizontal = useMemo(() => {
    return imagePosition === Side.LEFT || imagePosition === Side.RIGHT;
  }, [imagePosition]);

  const imageClassName = useMemo(() => {
    if (imageWidth === ImageWidth.FULL_WIDTH) {
      return 'w-full';
    }
  }, [imageWidth]);

  const linkDecorator = withLinkDecorator && (
    <div
      className={clsxm(
        'my-8 mb-4 flex h-1 w-12 flex-1 rounded-full bg-primary',
        textAlignment === HorizontalAlignment.CENTER && 'mx-auto',
        decoratorAlignmentMap[textAlignment],
        cardStyle === CardStyle.PROFILE_STYLE && 'my-2'
      )}
    />
  );

  const cardImage = image && (
    <HygraphImage
      {...image}
      className={clsxm(
        'mx-auto',
        cardStyle && imagePosition && imageClassMap[cardStyle]?.[imagePosition],
        image.className
      )}
      imgClassName={imageClassName}
    />
  );

  const cardCaption = caption && (
    <Typography.Subtitle2
      as='h6'
      className={clsxm(
        image ? 'mb-4' : 'my-4',
        'text-[20px] font-medium',
        cardStyle === CardStyle.PROFILE_STYLE &&
          'mb-0 text-[14px] font-medium leading-[130%] tracking-[.035px]'
      )}
    >
      {interpolate(caption)}
    </Typography.Subtitle2>
  );

  const cardTitle = title && (
    <Typography.Heading5
      className={clsxm(
        'mb-4',
        cardStyle === CardStyle.PROFILE_STYLE &&
          'mb-0 text-[18px] leading-[130%] -tracking-[.36px]'
      )}
    >
      {interpolate(title)}
    </Typography.Heading5>
  );

  const cardCopy =
    typeof body === 'string' ? (
      <Typography.Subtitle2 className='mb-4'>{body}</Typography.Subtitle2>
    ) : (
      body?.content && <HygraphRichText content={body.content} />
    );

  const cardLinkLink = link && (
    <div>
      <Link
        {...link}
        className={clsxm(isHorizontal && 'mt-4 w-auto', link.className)}
      />
    </div>
  );

  const Wrapper = cardLink ? 'a' : 'div';
  const wrapperProps = cardLink
    ? { href: cardLink.href, target: '_blank' }
    : {};

  const hasSomeText = !!(
    cardCaption ||
    cardTitle ||
    cardCopy ||
    linkDecorator ||
    cardLinkLink
  );

  const commonAwardQuoteClasses = clsxm(
    'absolute top-1/2 -translate-y-1/2',
    'h-[118px] w-[43px]'
  );

  return (
    <Wrapper
      {...wrapperProps}
      className={clsxm(
        'flex h-full overflow-hidden',
        isInSidebar && 'h-auto',
        imagePositionClass,
        cardStyle ? CardStylesClassNames[cardStyle] : 'bg-white',
        hasImage && cardStyle !== CardStyle.PROFILE_STYLE && 'p-0',
        className
      )}
    >
      {cardStyle === CardStyle.AWARD_QUOTE && (
        <>
          {/* left-3 and right-3 are used to match the hasSomeText y padding */}
          <AwardQuoteBgLeft
            className={clsxm(commonAwardQuoteClasses, 'left-3')}
          />
          <AwardQuoteBgRight
            className={clsxm(commonAwardQuoteClasses, 'right-3')}
          />
        </>
      )}

      {cardImage}

      {hasSomeText && (
        <div
          className={clsxm(
            'max-w-full break-words px-5 py-3 sm:p-5',
            isHorizontal && 'flex flex-1 flex-col justify-center',
            textAlignmentMap[textAlignment]
          )}
        >
          {cardStyle === CardStyle.PROFILE_STYLE ? (
            <>
              {cardTitle}
              {cardCaption}
              {linkDecorator && linkDecorator}
              {cardCopy}
            </>
          ) : (
            <>
              {cardCaption}
              {cardTitle}
              {cardCopy}
              {linkDecorator && linkDecorator}
              {cardLinkLink}
            </>
          )}
        </div>
      )}
    </Wrapper>
  );
};
export default Card;
